/**
 * Half-open occupancy maths for the timeline/floor views (DOMAIN.md §B.4/§B.2 `OccupiedWindow`,
 * §C.7). Every window is `[start, end)` in epoch seconds; a table freed at 20:00 seats a 20:00
 * booking — TOUCHING IS NOT OVERLAP, by construction. All pure + tz-correct so the cross-midnight
 * and boundary-touching acceptance cases are unit-provable without rendering.
 */

export interface OccupiedWindow {
  /** Epoch seconds, inclusive. */
  startSec: number;
  /** Epoch seconds, exclusive (half-open). */
  endSec: number;
}

export function windowOf(startSec: number, durationSec: number): OccupiedWindow {
  return { startSec, endSec: startSec + Math.max(0, durationSec) };
}

/** Half-open overlap: `a.start < b.end && b.start < a.end`. Boundary-touching → `false`. */
export function overlaps(a: OccupiedWindow, b: OccupiedWindow): boolean {
  return a.startSec < b.endSec && b.startSec < a.endSec;
}

/** Civil-date difference in days between two ISO `yyyy-MM-dd` strings (b - a). Timezone-free. */
export function dayDiff(aIso: string, bIso: string): number {
  const a = aIso.split("-");
  const b = bIso.split("-");
  const utcA = Date.UTC(Number(a[0]), Number(a[1]) - 1, Number(a[2]));
  const utcB = Date.UTC(Number(b[0]), Number(b[1]) - 1, Number(b[2]));
  return Math.round((utcB - utcA) / 86_400_000);
}

export interface WallClock {
  /** Venue-local civil date `yyyy-MM-dd`. */
  date: string;
  /** Minutes since local midnight (0..1439). */
  minutes: number;
}

/** The venue-local wall clock of an epoch instant, via `Intl` (no date lib, ADR-0013). */
export function wallClock(startSec: number, timeZone: string): WallClock {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(startSec * 1000));
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? "00";
  const hour = get("hour") === "24" ? "00" : get("hour"); // some engines emit 24 for midnight
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    minutes: Number(hour) * 60 + Number(get("minute")),
  };
}

/**
 * Minutes from the START of a `serviceDay` (its local midnight) for an instant — the timeline X
 * position. A 01:00 slot whose service day is the PREVIOUS date lands at 1500 (25h), so it renders
 * on that service day's row past the midnight gridline (§B.4 cross-midnight anchor), never on the
 * next day. Same-day slots return their plain minute-of-day.
 */
export function minutesFromServiceDay(
  startSec: number,
  serviceDay: string,
  timeZone: string,
): number {
  const wc = wallClock(startSec, timeZone);
  return dayDiff(serviceDay, wc.date) * 1440 + wc.minutes;
}

export interface TimelineBlock<T> {
  item: T;
  /** Minutes from service-day midnight. */
  startMin: number;
  endMin: number;
  /** True when this block half-open-overlaps a same-lane sibling (a real double-booking). */
  conflict: boolean;
}

/**
 * Lay a lane's bookings onto the minute axis and flag genuine overlaps (half-open). Two
 * boundary-touching bookings on one table never flag (they share only the instant `end == start`).
 */
export function layoutLane<T>(
  items: readonly T[],
  serviceDay: string,
  timeZone: string,
  startSecOf: (item: T) => number,
  durationSecOf: (item: T) => number,
): TimelineBlock<T>[] {
  const blocks = items.map((item) => {
    const startSec = startSecOf(item);
    const win = windowOf(startSec, durationSecOf(item));
    const startMin = minutesFromServiceDay(startSec, serviceDay, timeZone);
    const endMin = startMin + (win.endSec - win.startSec) / 60;
    return { item, startMin, endMin, win, conflict: false };
  });
  for (let i = 0; i < blocks.length; i++) {
    const bi = blocks[i];
    if (!bi) continue;
    for (let j = i + 1; j < blocks.length; j++) {
      const bj = blocks[j];
      if (!bj) continue;
      if (overlaps(bi.win, bj.win)) {
        bi.conflict = true;
        bj.conflict = true;
      }
    }
  }
  return blocks.map(({ item, startMin, endMin, conflict }) => ({
    item,
    startMin,
    endMin,
    conflict,
  }));
}
